"use client";

import { Button } from "@heroui/button";
import { Listbox, ListboxItem } from "@heroui/listbox";
import {
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenu,
  NavbarMenuToggle,
  Navbar as NextUINavbar,
} from "@heroui/navbar";
import { Tab, Tabs } from "@heroui/tabs";
import { default as Link, default as NextLink } from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";

import { Logo } from "@/components/icons";
import { siteConfig } from "@/config/site";
import useMenuStore from "@/store/use-menu-store";
import styles from "@/styles/style.module.scss";

export const Navbar = () => {
  const pathname = usePathname();
  const activeMenu = useMenuStore((state) => state.activeMenu);
  const setActiveMenu = useMenuStore((state) => state.setActiveMenu);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Find the blog tab key (adjust 'blog' if your key is different)
  const blogTabKey = siteConfig.navItems.find((item) =>
    item.label.includes("Blog"),
  )?.href;
  const aboutTabKey = siteConfig.navItems.find((item) =>
    item.label.includes("About Us"),
  )?.href;

  // Determine which tab should be active
  const selectedTab = pathname.includes("/posts")
    ? blogTabKey
    : pathname.includes("/about-us")
      ? aboutTabKey
      : activeMenu;

  const onChangeMenu = (key: React.Key) => {
    if (key === blogTabKey || key === aboutTabKey) {
      // Navigate to the blog or about us page
    } else {
      // Scroll to the element
      const element = document.getElementById(String(key));
      const y = element
        ? element.getBoundingClientRect().top + window.scrollY
        : 0;

      if (pathname === "/") {
        window.scroll({
          top: y - 80,
          behavior: "smooth",
        });
      } else {
        window.location.href = "/#" + String(key);
      }
    }
    setIsMenuOpen(false);
    setActiveMenu(key as string);
  };

  if (
    pathname == "/" ||
    pathname.includes("/posts") ||
    pathname == "/about-us/"
  ) {
    return (
      <NextUINavbar
        className="border-b py-2 bg-white"
        isMenuOpen={isMenuOpen}
        maxWidth="xl"
        position="sticky"
        onMenuOpenChange={setIsMenuOpen}
      >
        <NavbarContent justify="start">
          <NavbarBrand>
            <NextLink href="/">
              <Logo customClass="w-36 lg:w-full" />
            </NextLink>
          </NavbarBrand>
        </NavbarContent>

        <NavbarContent className="hidden lg:flex" justify="center">
          <NavbarItem className="hidden lg:flex">
            <Tabs
              key={"tabs-menu"}
              aria-label="Tabs sizes"
              className="gap-0"
              selectedKey={selectedTab}
              size={"lg"}
              onSelectionChange={(key: React.Key | null) => {
                if (key) {
                  onChangeMenu(key);
                }
              }}
            >
              {siteConfig.navItems.map((item) => (
                <Tab
                  key={item.href}
                  className="px-10"
                  title={item.label}
                  {...(item.isUrl ? { href: item.href } : {})}
                />
              ))}
            </Tabs>
          </NavbarItem>
        </NavbarContent>

        <NavbarContent className="hidden lg:flex" justify="end">
          <NavbarItem className="hidden lg:flex">
            <Button
              as={Link}
              className="bg-brand text-white h-[46px] text-sm px-[30px]"
              href="/book-a-call"
              title="Book a Call"
              variant="flat"
            >
              Book a Call
            </Button>
          </NavbarItem>
        </NavbarContent>

        <NavbarContent className="lg:hidden pl-4" justify="end">
          <NavbarMenuToggle />
        </NavbarContent>

        <NavbarMenu>
          <div className="flex flex-col justify-between h-dvh py-5 gap-4">
            <Listbox
              aria-label="Listbox menu with icons"
              variant="faded"
              onAction={(key) => onChangeMenu(key)}
            >
              {siteConfig.navItems.map((item) => (
                <ListboxItem key={item.href}>{item.label}</ListboxItem>
              ))}
            </Listbox>
            <Button
              as={Link}
              className={`${styles.button_primary} h-[46px]`}
              href="/book-a-call"
              style={{ fontSize: "14px" }}
              title="Book a Call"
              variant="flat"
            >
              Book a Call
            </Button>
          </div>
        </NavbarMenu>
      </NextUINavbar>
    );
  }

  return (
    <NextUINavbar className="border-b py-2" maxWidth="xl" position="sticky">
      <NavbarContent justify="start">
        <NavbarBrand>
          <NextLink href="/">
            <Logo customClass="w-36 lg:w-full" />
          </NextLink>
        </NavbarBrand>
      </NavbarContent>
    </NextUINavbar>
  );
};
